import { getAntiforgeryToken } from "../api/http";
import {
  rights,
  type ContactCommercial,
  type ContactContext,
  type ContactDetailResult,
  type ContactImportFileResult,
  type ContactImportResult,
  type ContactImportTagChoice,
  type ContactPageResult,
  type ContactPerson,
  type ContactQuery,
  type ContactRights,
} from "./contactModel";
import { ContactRequestFailure } from "./tagCatalogClient";

// Cliente del directorio de Contactos (ContactDirectoryClient.cs del portal actual): cookie viva y antiforgery, y una
// respuesta tardía de otra empresa, persona, sesión o alcance nunca llega a la pantalla. El servidor decide el acceso;
// estas comprobaciones solo evitan mostrar datos de un contexto que ya no es el de la pestaña.

export { ContactRequestFailure };

/** Un contexto confirmado hace menos de esto se reutiliza antes de salir (C9-05); la comprobación de después, siempre. */
const recentContext = 15_000;

type Sent = { rights: ContactRights; scope: string; session: string; owner: string; path: string };

export type ContactExtract = { query: ContactQuery; scope: string; format: string; selectedIds: number[] | null };
export type ContactImportPreview = {
  text: string;
  separator: string;
  mapping: Record<string, number>;
  bheliExport: boolean;
};
export type ContactSaveRequest = {
  firstName: string;
  lastName: string;
  expectedVersion: number | null;
  commercial: ContactCommercial;
};

export class ContactDirectoryClient {
  tenant: number;
  user: number;
  currentRights: ContactRights | null = null;
  currentSessionStamp: string | null = null;
  currentScope: string | null = null;
  private contextSequence = 0;
  private acceptedContext = 0;
  private confirmedAt = 0;
  private confirmedFor = "";

  constructor(tenant: number, user: number) {
    this.tenant = tenant;
    this.user = user;
  }

  /** Pide el contexto de la persona (sesión, empresa, permisos y revisión del alcance). */
  async ensureContext(signal?: AbortSignal): Promise<void> {
    const tenant = this.tenant,
      user = this.user,
      sequence = ++this.contextSequence;
    const response = await fetch("/api/portal/contacts/context", { credentials: "same-origin", signal });
    if (!response.ok)
      throw new ContactRequestFailure(
        response.status,
        response.status === 401 || response.status === 403 ? "SESSION_INVALID" : "DEPENDENCY_UNAVAILABLE",
      );
    const context = (await response.json()) as ContactContext;
    if (context?.organizationId !== tenant || context.userId !== user || this.tenant !== tenant || this.user !== user)
      throw new ContactRequestFailure(409, "TENANT_CHANGED");
    if (sequence >= this.acceptedContext) {
      this.acceptedContext = sequence;
      this.currentRights = context.access ? rights(context.access) : null;
      this.currentScope = context.scopeRevision ?? null;
      this.currentSessionStamp = context.sessionStamp ?? null;
      this.confirmedAt = performance.now();
      this.confirmedFor = `${tenant}:${user}`;
    }
    if (this.currentRights?.canRead !== true) throw new ContactRequestFailure(403, "ACCESS_REVOKED");
  }

  /** El contexto recién confirmado (misma persona y empresa, hace menos de 15 s) o uno nuevo. */
  ensureRecentContext(signal?: AbortSignal): Promise<void> {
    if (
      !this.currentRights ||
      this.confirmedFor !== `${this.tenant}:${this.user}` ||
      performance.now() - this.confirmedAt >= recentContext
    )
      return this.ensureContext(signal);
    return this.currentRights.canRead
      ? Promise.resolve()
      : Promise.reject(new ContactRequestFailure(403, "ACCESS_REVOKED"));
  }

  list(query: ContactQuery, signal?: AbortSignal): Promise<ContactPageResult> {
    return this.send("query", query, signal);
  }

  /** La primera lista sale a la vez que su contexto (R5); no se entrega si el contexto falla. */
  async listWithContext(context: Promise<void>, query: ContactQuery, signal?: AbortSignal): Promise<ContactPageResult> {
    const owner = `${this.tenant}:${this.user}`;
    const sending = new AbortController();
    signal?.addEventListener("abort", () => sending.abort(), { once: true });
    const pending = fetch("/api/portal/contacts/query", {
      method: "POST",
      credentials: "same-origin",
      signal: sending.signal,
      headers: this.headers(true),
      body: JSON.stringify(query),
    });
    try {
      await context;
    } catch (error) {
      sending.abort();
      await pending.catch(() => undefined);
      throw error;
    }
    const sent: Sent = {
      rights: this.currentRights!,
      scope: this.currentScope ?? "",
      session: this.currentSessionStamp ?? "",
      owner,
      path: "/api/portal/contacts/query",
    };
    return this.result<ContactPageResult>(await pending, sent, signal);
  }

  tags(search: string, selected: string[], signal?: AbortSignal): Promise<{ items: string[]; hasMore: boolean }> {
    return this.send("tags", { search, selected, limit: 100 }, signal);
  }
  get(id: number, signal?: AbortSignal, before?: number | null): Promise<ContactDetailResult> {
    return this.read(`${id}` + (before !== undefined && before !== null ? `?before=${before}` : ""), signal);
  }
  getImport(id: string, signal?: AbortSignal, page = 1, pendingOnly = false): Promise<ContactImportResult> {
    // Los booleanos como los escribe .NET en la dirección (False/True).
    return this.read(`imports/${id}?page=${page}&pendingOnly=${pendingOnly ? "True" : "False"}`, signal);
  }
  file(request: { text: string; separator: string }, signal?: AbortSignal): Promise<ContactImportFileResult> {
    return this.send("imports/file", request, signal);
  }
  async importReport(id: string, signal?: AbortSignal): Promise<string> {
    const { response, sent } = await this.request("GET", `imports/${id}/report`, null, null, signal);
    await this.check(response, sent, signal);
    return response.text();
  }
  save(
    id: number | null,
    request: ContactSaveRequest,
    key: string,
    signal?: AbortSignal,
  ): Promise<{ contact: ContactPerson; operationId: string }> {
    return this.send(id !== null ? `${id}/save` : "create", request, signal, key);
  }
  matches(commercial: ContactCommercial, except: number | null, signal?: AbortSignal): Promise<ContactPerson[]> {
    return this.send("matches", { commercial, exceptId: except }, signal);
  }
  preview(request: ContactImportPreview, key: string, signal?: AbortSignal): Promise<ContactImportResult> {
    return this.send("imports/preview", request, signal, key);
  }
  resolveImportTags(
    id: string,
    request: { expectedVersion: number; choices: ContactImportTagChoice[] },
    signal?: AbortSignal,
  ): Promise<ContactImportResult> {
    return this.send(`imports/${id}/tags`, request, signal);
  }
  async commit(id: string, signal?: AbortSignal, expectedVersion?: number): Promise<ContactImportResult> {
    const { response, sent } = await this.request("POST", `imports/${id}/commit`, null, null, signal, expectedVersion);
    return this.result(response, sent, signal);
  }
  async extract(request: ContactExtract, signal?: AbortSignal): Promise<string> {
    const { response, sent } = await this.request("POST", "extract", JSON.stringify(request), null, signal);
    await this.check(response, sent, signal);
    if (this.currentRights?.canExport !== true) throw new ContactRequestFailure(403, "ACCESS_REVOKED");
    return (await response.text()).replace(/^\uFEFF/, "");
  }
  /** Exportar en flujo (R5): el archivo llega por partes; las comprobaciones de siempre corren antes del primer bloque. */
  async extractDownload(
    request: ContactExtract,
    consume: (body: ReadableStream<Uint8Array>) => Promise<number>,
    signal?: AbortSignal,
  ): Promise<number> {
    const { response, sent } = await this.request("POST", "extract", JSON.stringify(request), null, signal);
    await this.check(response, sent, signal);
    if (this.currentRights?.canExport !== true) throw new ContactRequestFailure(403, "ACCESS_REVOKED");
    return consume(response.body!);
  }

  private headers(json: boolean): Record<string, string> {
    const headers: Record<string, string> = { "X-Bheli-Contact-Tenant": String(this.tenant) };
    if (json) headers["Content-Type"] = "application/json";
    const token = getAntiforgeryToken();
    if (token) headers["X-CSRF-TOKEN"] = token;
    return headers;
  }

  private async send<T>(path: string, body: unknown, signal?: AbortSignal, key?: string): Promise<T> {
    const { response, sent } = await this.request("POST", path, JSON.stringify(body), key ?? null, signal);
    return this.result<T>(response, sent, signal);
  }
  private async read<T>(path: string, signal?: AbortSignal): Promise<T> {
    const { response, sent } = await this.request("GET", path, null, null, signal);
    return this.result<T>(response, sent, signal);
  }

  private async request(
    method: "GET" | "POST",
    path: string,
    body: string | null,
    key: string | null,
    signal?: AbortSignal,
    expectedVersion?: number,
  ): Promise<{ response: Response; sent: Sent }> {
    // Las lecturas salen con el contexto recién confirmado; lo que escribe o exporta lo vuelve a pedir antes de salir.
    await (method === "GET" || path === "query" || path === "tags" || path === "matches"
      ? this.ensureRecentContext(signal)
      : this.ensureContext(signal));
    const current = this.currentRights;
    if (
      (path.startsWith("imports") && (current?.canImport !== true || !current.canWrite || !current.globalScope)) ||
      (path === "extract" && current?.canExport !== true) ||
      ((path === "create" || path.endsWith("/save")) && current?.canWrite !== true)
    )
      throw new ContactRequestFailure(403, "ACCESS_REVOKED");
    const sent: Sent = {
      rights: current!,
      scope: this.currentScope ?? "",
      session: this.currentSessionStamp ?? "",
      owner: `${this.tenant}:${this.user}`,
      path: "/api/portal/contacts/" + path.split("?")[0],
    };
    const headers: Record<string, string> =
      method === "GET" ? { "X-Bheli-Contact-Tenant": String(this.tenant) } : this.headers(body !== null);
    if (expectedVersion !== undefined) headers["If-Match"] = `"${expectedVersion}"`;
    if (key) headers["Idempotency-Key"] = key;
    const response = await fetch("/api/portal/contacts/" + path, {
      method,
      credentials: "same-origin",
      signal,
      headers,
      body: body ?? undefined,
    });
    return { response, sent };
  }

  private async result<T>(response: Response, sent: Sent, signal?: AbortSignal): Promise<T> {
    await this.check(response, sent, signal);
    const text = await response.text();
    let value: T;
    try {
      value = JSON.parse(text) as T;
    } catch {
      throw new ContactRequestFailure(502, "DEPENDENCY_UNAVAILABLE");
    }
    if (value === null || value === undefined) throw new ContactRequestFailure(502, "DEPENDENCY_UNAVAILABLE");
    const scope = (value as { scopeRevision?: string | null }).scopeRevision;
    const scoped = sent.path.endsWith("/query") || /\/contacts\/\d+$/.test(sent.path);
    if (scoped && scope !== null && scope !== undefined && scope !== this.currentScope)
      throw new ContactRequestFailure(409, "CONTACT_SCOPE_CHANGED");
    return value;
  }

  /** Después de cada respuesta: el contexto otra vez y que la empresa, la persona, la sesión y el alcance sigan iguales. */
  private async check(response: Response, sent: Sent, signal?: AbortSignal): Promise<void> {
    if (!response.ok) {
      const failure = (await response.json().catch(() => null)) as { code?: string } | null;
      throw new ContactRequestFailure(response.status, failure?.code ?? "DEPENDENCY_UNAVAILABLE");
    }
    await this.ensureContext(signal);
    const imports = sent.path.includes("/imports/");
    if (sent.owner !== `${this.tenant}:${this.user}`) throw new ContactRequestFailure(409, "TENANT_CHANGED");
    if (sent.session !== (this.currentSessionStamp ?? "")) throw new ContactRequestFailure(401, "SESSION_INVALID");
    // Los datos propios de la importación dependen de su autoridad, no del alcance de Messaging.
    if (!imports && sent.scope !== (this.currentScope ?? ""))
      throw new ContactRequestFailure(409, "CONTACT_SCOPE_CHANGED");
    const current = this.currentRights;
    if (
      (imports && (current?.canImport !== true || !current.canWrite || !current.globalScope)) ||
      (sent.path.endsWith("/save") && current?.canWrite !== true)
    )
      throw new ContactRequestFailure(403, "ACCESS_REVOKED");
    if (sent.rights.globalScope !== current!.globalScope) throw new ContactRequestFailure(403, "ACCESS_REVOKED");
  }
}

/** Uno por persona y empresa en la página, fuera de Contactos (el panel del contacto del Chat). */
const shared = new Map<string, ContactDirectoryClient>();
export function contactClient(tenant: number, user: number): ContactDirectoryClient {
  const key = `${tenant}:${user}`;
  let client = shared.get(key);
  if (!client) shared.set(key, (client = new ContactDirectoryClient(tenant, user)));
  return client;
}
